/**
 * PantoneInput — free-text Pantone code input with a simulated-swatch picker.
 *
 * A lightweight inline combobox (not shadcn Combobox/Popover: the suggestions
 * must not steal focus from the input, because callers commit on blur). Typing
 * filters the curated PANTONE_LIBRARY by code or colour name; picking a row
 * writes the canonical code. Free text that matches nothing is kept verbatim —
 * the library is a convenience, not a validation gate.
 *
 * Swatches are sRGB approximations (see pantone.ts) — the footer hint says so,
 * visibly, per the no-information-only-tooltips rule (DESIGN.md §10).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  findPantone,
  searchPantone,
  type PantoneEntry,
} from '../utils/pantone';

export function PantoneInput({
  value,
  onChange,
  onPick,
  onCommit,
  onCancel,
  placeholder,
  className,
  autoFocus,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  /** A suggestion was chosen (fires after onChange with the canonical code). */
  onPick?: (entry: PantoneEntry) => void;
  /** Enter (with no highlighted suggestion) or blur. */
  onCommit?: () => void;
  /** Escape. */
  onCancel?: () => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation('productDev');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const options = open ? searchPantone(value) : [];
  const resolved = findPantone(value);

  // Reset keyboard highlight whenever the option set changes shape.
  useEffect(() => setActiveIdx(-1), [value, open]);

  const pick = (entry: PantoneEntry) => {
    onChange(entry.code);
    setOpen(false);
    onPick?.(entry);
    inputRef.current?.focus();
  };

  return (
    <div className={cn('relative', className)}>
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          onCommit?.();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActiveIdx((i) => Math.min(i + 1, options.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, -1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const active = activeIdx >= 0 ? options[activeIdx] : undefined;
            if (active) {
              pick(active);
            } else {
              setOpen(false);
              onCommit?.();
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            if (open) {
              setOpen(false);
            } else {
              onCancel?.();
            }
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        className="h-8 pr-7 font-mono text-xs"
      />
      {/* Live swatch preview for whatever is currently typed. */}
      {resolved && (
        <span
          className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-sm border border-black/10"
          style={{ backgroundColor: resolved.hex }}
          aria-hidden="true"
        />
      )}
      {open && options.length > 0 && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          <ul className="max-h-56 overflow-y-auto py-1">
            {options.map((opt, i) => (
              <li key={opt.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === activeIdx}
                  // Prevent the input's blur-commit from firing mid-pick.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(opt)}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={cn(
                    'flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs',
                    i === activeIdx ? 'bg-accent text-accent-foreground' : '',
                  )}
                >
                  <span
                    className="h-4 w-4 shrink-0 rounded-sm border border-black/10"
                    style={{ backgroundColor: opt.hex }}
                    aria-hidden="true"
                  />
                  <span className="shrink-0 font-mono text-[11px]">{opt.code}</span>
                  <span className="min-w-0 flex-1 truncate text-muted-foreground">
                    {opt.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <p className="border-t bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground">
            {t('pantone.simulationHint')}
          </p>
        </div>
      )}
    </div>
  );
}
